import React, { ReactNode, Dispatch, SetStateAction, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/shadcn/card";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui/shadcn/table";
import { Button } from "@/components/ui/shadcn/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import {
  AlertCircle,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Filtering } from "./Filtering";
import {
  ColumnVisibility,
  useColumnVisibility,
} from "@/components/dashboard/data-table/ColumnVisibility";
import { useTableSorting } from "@/components/dashboard/data-table/SortableTableHeader";
import {
  DateEnhancedQuote,
  DateEnhancedAcceptedQuote,
  Quote,
  QuoteStatus,
  AcceptedQuoteStatus,
} from "@/types/quotes";
import { DateEnhancedTrade } from "@/types/trades";
import { TooltipProvider } from "@/components/ui/shadcn/tooltip";
import { CreateQuoteDialog } from "@/components/dashboard/tabs/components/CreateQuoteDialog";
import { cn } from "@/lib/tailwind";

type QuoteType =
  | DateEnhancedQuote
  | DateEnhancedAcceptedQuote
  | DateEnhancedTrade;

interface PaginationProps {
  page: number;
  setPage: (page: number) => void;
  size: number;
  setSize: (size: number) => void;
  total: number;
  pages: number;
}

interface BaseDataTableProps<T extends QuoteType> {
  title: string;
  description: string;
  data: T[];
  filteredData: T[];
  setFilteredData: Dispatch<SetStateAction<T[]>>;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onExportCSV: () => void;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  selectedCoins?: string[];
  setSelectedCoins?: Dispatch<SetStateAction<string[]>>;
  selectedFiatCurrencies?: string[];
  setSelectedFiatCurrencies?: Dispatch<SetStateAction<string[]>>;
  selectedVmarketIds?: number[];
  setSelectedVmarketIds?: Dispatch<SetStateAction<number[]>>;
  selectedStatuses?: T extends DateEnhancedAcceptedQuote
    ? AcceptedQuoteStatus[]
    : QuoteStatus[];
  setSelectedStatuses?: Dispatch<
    SetStateAction<
      T extends DateEnhancedAcceptedQuote
        ? AcceptedQuoteStatus[]
        : QuoteStatus[]
    >
  >;
  columns: Record<string, { label: string; visible: boolean }>;
  default_columns: Record<string, { label: string; visible: boolean }>;
  storageKey: string;
  sortableKeys: string[];
  defaultSortKey: string;
  defaultSortDirection: "asc" | "desc";
  renderTableHeader: (props: {
    sortConfig: { key: string; direction: "asc" | "desc" | null };
    handleSort: (key: string) => void;
    isColumnVisible: (key: string) => boolean;
  }) => ReactNode;
  renderTableRow: (item: T) => ReactNode;
  withQuoteTypeBoxes?: boolean;
  isAcceptedQuotes?: boolean;
  showCreateQuoteButton?: boolean;
  onQuoteCreated?: (quote: Quote) => void;
  pagination?: PaginationProps;
  onResetColumns?: () => void;
  setColumns: (columns: Record<string, { label: string; visible: boolean }>) => void;
}

// Add these utility functions before the BaseDataTable component
const sortByKey = <T extends QuoteType>(
  items: T[],
  key: keyof T,
  direction: "asc" | "desc"
): T[] => {
  return [...items].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];

    if (key.toString().includes("expiration")) {
      return direction === "desc"
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
        ? 1
        : -1;
    }

    // Handle date fields
    if (key.toString().includes("date")) {
      return direction === "asc"
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
        ? 1
        : -1;
    }

    // Handle numeric fields
    if (key.toString().includes("numeric") || key === "fees_bps") {
      const aNum = parseFloat(aValue as string);
      const bNum = parseFloat(bValue as string);
      return direction === "asc" ? aNum - bNum : bNum - aNum;
    }

    // Handle nested fields
    if (key === "client_name") {
      return direction === "asc"
        ? a.client.name > b.client.name
          ? 1
          : -1
        : a.client.name < b.client.name
        ? 1
        : -1;
    }

    if (key === "asset_pair") {
      // First sort by base asset code
      const baseCompare =
        direction === "asc"
          ? a.vmarket.base.code.localeCompare(b.vmarket.base.code)
          : b.vmarket.base.code.localeCompare(a.vmarket.base.code);
      if (baseCompare !== 0) return baseCompare;
      // If base assets are the same, sort by counter asset code
      return direction === "asc"
        ? a.vmarket.counter.code.localeCompare(b.vmarket.counter.code)
        : b.vmarket.counter.code.localeCompare(a.vmarket.counter.code);
    }

    // Handle string fields
    return direction === "asc"
      ? aValue > bValue
        ? 1
        : -1
      : aValue < bValue
      ? 1
      : -1;
  });
};

const filterData = <T extends QuoteType>(
  data: T[],
  filters: {
    searchTerm?: string;
    selectedStatuses?: T extends DateEnhancedAcceptedQuote
      ? AcceptedQuoteStatus[]
      : QuoteStatus[];
    isAcceptedQuotes?: boolean;
    selectedVmarketIds?: number[];
  }
): T[] => {
  let filtered = data;

  // Filter by search term
  if (filters.searchTerm) {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(
      (item) =>
        item.client.name.toLowerCase().includes(searchLower) ||
        item.client.email.toLowerCase().includes(searchLower)
    );
  }

  // Filter by status
  if (filters.selectedStatuses) {
    if (filters.selectedStatuses.length === 0){
      return [];
    }
    filtered = filtered.filter((item) => {
      if ('status' in item) {
        if (filters.isAcceptedQuotes) {
          return (filters.selectedStatuses as AcceptedQuoteStatus[]).includes(
            item.status as AcceptedQuoteStatus
          );
        }
        return (filters.selectedStatuses as QuoteStatus[]).includes(
          item.status as QuoteStatus
        );
      }
      return false;
    });
  }

  // Filter by vmarket IDs
  if (filters.selectedVmarketIds?.length) {
    filtered = filtered.filter((item) => {
      if ('vmarket' in item) {
        return filters.selectedVmarketIds?.includes(item.vmarket.id);
      }
      return false;
    });
  }

  return filtered;
};

export function BaseDataTable<T extends QuoteType>({
  title,
  description,
  data,
  filteredData,
  setFilteredData,
  loading,
  error,
  onRetry,
  onExportCSV,
  searchTerm,
  setSearchTerm,
  selectedCoins,
  setSelectedCoins,
  selectedFiatCurrencies,
  setSelectedFiatCurrencies,
  selectedVmarketIds,
  setSelectedVmarketIds,
  selectedStatuses,
  setSelectedStatuses,
  default_columns,
  columns,
  storageKey,
  sortableKeys,
  defaultSortKey,
  defaultSortDirection,
  renderTableHeader,
  renderTableRow,
  withQuoteTypeBoxes = false,
  isAcceptedQuotes = false,
  showCreateQuoteButton = false,
  onQuoteCreated,
  pagination,
  onResetColumns,
  setColumns,
}: BaseDataTableProps<T>) {
  // Use the sorting hook
  const { sortConfig, handleSort } = useTableSorting(
    defaultSortKey,
    defaultSortDirection
  );

  // Replace the useEffect for filtering and sorting with this simplified version
  useEffect(() => {
    let filtered = filterData(data, {
      searchTerm,
      selectedStatuses,
      isAcceptedQuotes,
      selectedVmarketIds,
    });

    // Apply sorting if configured
    if (sortConfig.direction) {
      filtered = sortByKey(
        filtered,
        sortConfig.key as keyof T,
        sortConfig.direction
      );
    }

    setFilteredData(filtered);
  }, [
    data,
    searchTerm,
    selectedStatuses,
    selectedVmarketIds,
    sortConfig,
    setFilteredData,
    isAcceptedQuotes,
  ]);

  const renderPagination = () => {
    if (!pagination) return null;

    const { page, setPage, size, setSize, total, pages } = pagination;

    const pageSizeOptions = [10, 25, 50, 100];

    return (
      <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-800 px-4 py-3">
        {/* Left side: Page info */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            Showing {(page - 1) * size + 1} to {Math.min(page * size, total)} of{" "}
            {total} entries
          </span>
        </div>

        {/* Right side: Controls */}
        <div className="flex items-center gap-6">
          {/* Page size selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              Rows per page
            </span>
            <Select
              value={size.toString()}
              onValueChange={(value) => {
                setSize(parseInt(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={`page-size-${option}`} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pagination controls */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="h-8 w-8 p-0 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: pages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={`page-${pageNum}`}
                  onClick={() => setPage(pageNum)}
                  className={cn(
                    "min-w-[2rem] h-8 px-2 text-sm font-medium transition-colors rounded-md",
                    pageNum === page
                      ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-50"
                      : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === pages}
              className="h-8 w-8 p-0 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {showCreateQuoteButton && onQuoteCreated && (
            <CreateQuoteDialog key="create-quote-dialog" onQuoteCreated={onQuoteCreated} />
          )}
          <div className="flex gap-2">
            <ColumnVisibility
              key="column-visibility"
              columns={columns}
              onColumnVisibilityChange={setColumns}
              initialConfig={default_columns}
              onReset={onResetColumns}
            />
            <Button
              key="export-csv"
              variant="outline"
              onClick={onExportCSV}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isAcceptedQuotes ? (
          <Filtering
            key="accepted-quotes-filtering"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedVmarketIds={selectedVmarketIds}
            setSelectedVmarketIds={setSelectedVmarketIds}
            selectedStatuses={selectedStatuses as AcceptedQuoteStatus[]}
            setSelectedStatuses={
              setSelectedStatuses as Dispatch<SetStateAction<AcceptedQuoteStatus[]>>
            }
            quotes={data}
            withQuoteTypeBoxes={withQuoteTypeBoxes}
            isAcceptedQuotes={isAcceptedQuotes}
          />
        ) : (
          <Filtering
            key="active-quotes-filtering"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedVmarketIds={selectedVmarketIds}
            setSelectedVmarketIds={setSelectedVmarketIds}
            selectedStatuses={selectedStatuses as QuoteStatus[]}
            setSelectedStatuses={
              setSelectedStatuses as Dispatch<SetStateAction<QuoteStatus[]>>
            }
            quotes={data}
            withQuoteTypeBoxes={withQuoteTypeBoxes}
            isAcceptedQuotes={isAcceptedQuotes}
          />
        )}

        {error && (
          <div key="error-message" className="mx-6 mb-4 flex items-center space-x-2 bg-red-50 border border-red-200 rounded-md p-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-600 text-sm font-medium">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={onRetry}
            >
              Retry
            </Button>
          </div>
        )}

        {loading ? (
          <div key="loading-spinner" className="p-6 flex justify-center items-center">
            <div className="flex flex-col items-center space-y-2">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div key="no-data" className="p-12 text-center">
            <p className="text-gray-500">
              No data found matching your filters.
            </p>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <Table>
              <TableHeader>
                <TableRow>
                  {renderTableHeader({
                    sortConfig,
                    handleSort,
                    isColumnVisible: (key: string) => columns[key]?.visible ?? true,
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <React.Fragment key={item.id || item.slug || Math.random()}>
                    {renderTableRow(item)}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        )}
        {renderPagination()}
      </CardContent>
    </Card>
  );
}
